import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import Input from '../UI/Input';
import Button from '../UI/Button';

interface ErrorObject {
  type?: string;
  message?: string;
  details?: any;
}

interface EmailPasswordFormProps {
  onSubmit: (data: { email: string; password: string; rememberMe?: boolean }) => Promise<void>;
  loading?: boolean;
  error?: string | ErrorObject | null;
  defaultEmail?: string;
  showForgotPassword?: boolean;
  accentColor?: 'blue' | 'green' | 'purple';
}

interface EmailPasswordFormData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

const EmailPasswordForm: React.FC<EmailPasswordFormProps> = ({
  onSubmit,
  loading = false,
  error,
  defaultEmail,
  showForgotPassword = true,
  accentColor = 'green',
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EmailPasswordFormData>({
    defaultValues: {
      email: defaultEmail || '',
      password: '',
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (defaultEmail) {
      setValue('email', defaultEmail);
    }
  }, [defaultEmail, setValue]);

  const onFormSubmit = async (data: EmailPasswordFormData) => {
    await onSubmit({
      email: data.email,
      password: data.password,
      rememberMe: data.rememberMe,
    });
  };

  const accentButtonClasses = {
    blue: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white',
    green: 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] text-white',
    purple: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white',
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
      {/* Email Input */}
      <div>
        <Input
          label="Email Address"
          type="email"
          autoComplete="email"
          placeholder="Enter your email"
          leftIcon={<Mail className="h-5 w-5" />}
          error={errors.email?.message}
          variant="mirsal"
          forceDarkMode={true}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
        />
      </div>

      {/* Password Input */}
      <div>
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          placeholder="Enter your password"
          leftIcon={<Lock className="h-5 w-5" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          }
          error={errors.password?.message}
          variant="mirsal"
          forceDarkMode={true}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters',
            },
          })}
        />
      </div>

      {/* Remember Me and Forgot Password */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            id="rememberMe"
            type="checkbox"
            {...register('rememberMe')}
            className="h-4 w-4 text-[#33FF00] focus:ring-[#33FF00] border-gray-400 rounded bg-white/5"
          />
          <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-300">
            Remember me for 30 days
          </label>
        </div>
        {showForgotPassword && (
          <div className="text-sm">
            <Link
              to="/auth/password-reset"
              className="font-medium text-[#33FF00] hover:text-[#66FF33] transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4">
          <p className="text-sm text-red-400">
            {typeof error === 'string' 
              ? error 
              : (error as any)?.message || (error as any)?.details || 'An error occurred'}
          </p>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading}
        className={`w-full py-3 ${accentButtonClasses[accentColor]} disabled:from-gray-600 disabled:to-gray-700 disabled:text-white rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none`}
        loading={loading}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>
    </form>
  );
};

export default EmailPasswordForm;

